import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetLaborListDropdownQuery } from '../../api/endpoints/compound';
import { useLanguage } from '../../contexts/LanguageContext';

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_DELAY = 500;
const PAGE_SIZE = 20;

const LaborDropdown = ({ value, onChange, className, disabled, placeholder, showClear = true, chassisNo = '', claimId }) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const [selectedLabor, setSelectedLabor] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const debounceTimerRef = useRef(null);

    const languageId = getCurrentLanguageId();
    
    const { data: laborList = [], isLoading } = useGetLaborListDropdownQuery({
        chassisNo: chassisNo,
        languageId: languageId,
        searchText: searchText,
        pageSize: PAGE_SIZE,
        claimId: claimId
    });

    const formatOptionLabel = (labor) => {
        const baseText = labor.text || '';
        if (labor.laborTime && labor.laborTime > 0) {
            return `${baseText} (${labor.laborTime} ${th('common.hour')})`;
        }
        return baseText;
    };

    const laborOptions = useMemo(
        () =>
            laborList.map((labor) => ({
                id: labor.id,
                text: labor.text,
                laborTime: labor.laborTime,
                displayValue: formatOptionLabel(labor)
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [laborList, th]
    );

    const handleSelectionChange = (e) => {
        setSelectedLabor(e.value);
        
        // Seçim yapıldığında filter'ı temizle
        setFilterValue('');
        setSearchText('');

        // Bekleyen timer varsa iptal et
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (onChange) {
            onChange(e.value);
        }
    };

    // Custom filter input onChange - Debounce ile API çağrısı
    const handleFilterInputChange = (value) => {
        // Input'ta hemen görünsün
        setFilterValue(value);

        // Önceki timer'ı iptal et
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Debounce sonrası API çağrısı yap (minimum karakter kontrolü ile)
        debounceTimerRef.current = setTimeout(() => {
            if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
                setSearchText(value);
            }
        }, DEBOUNCE_DELAY);
    };

    // Custom filter template - Dropdown'ın kendi filter'ını değil, bizim controlled input'umuzu kullan
    const filterTemplate = () => (
        <div className="p-dropdown-filter-container">
            <InputText
                value={filterValue}
                onChange={(e) => handleFilterInputChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={th('common.search')}
                autoFocus
                type="text"
                inputMode="text"
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    );

    // Cleanup: Component unmount olduğunda timer'ı temizle
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (laborOptions.length > 0 && value) {
            let found = null;

            // Check if value is an object with id property
            if (typeof value === 'object' && value.id) {
                found = laborOptions.find((option) => option.id === value.id);
            }
            // Check if value is just an id number
            else if (typeof value === 'number') {
                found = laborOptions.find((option) => option.id === value);
            }

            if (found) {
                setSelectedLabor(found);
            }
        } else if (!value) {
            setSelectedLabor(null);
        }
    }, [value, laborOptions]);

    return (
        <Dropdown
            value={selectedLabor}
            options={laborOptions}
            onChange={handleSelectionChange}
            optionLabel="displayValue"
            filter
            filterTemplate={filterTemplate}
            showClear={showClear}
            className={className}
            placeholder={placeholder || th('labor.laborSelectPlaceholder')}
            loading={isLoading}
            disabled={disabled}
            emptyFilterMessage={th('common.noResultsFound')}
            itemTemplate={(option) => <div>{option.displayValue}</div>}
        />
    );
};

export default LaborDropdown;
